import { useUserStore } from '@/utils/stores/useUserStore';
import RELEASE_VERSION from '@/utils/version';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { useAuthStore } from '../../../utils/stores/useAuthStore';
import { AdminUserManager } from '../components/AdminUserManager';
import { ExperimentalUserManager } from '../components/ExperimentalUserManager';

export const AdminDashboard: React.FC = () => {
  const { userData } = useUserStore();
  const { name, familyName, email } = userData;
  const { logout } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout(navigate);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <AdminDashboardHeader handleLogout={handleLogout} />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="space-y-6 sm:space-y-8">
          {/* Welcome Section */}
          <div className="text-center sm:text-left">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              Benvenuto nella Dashboard Admin
            </h1>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl">
              Manage platform access, experimental features, and administrative controls
            </p>
          </div>

          {/* Management Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Admin Users Section */}
            <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-600 text-lg">👑</span>
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-gray-900">
                      Admin Management
                    </CardTitle>
                    <CardDescription className="text-sm text-gray-600">
                      Manage administrative access and permissions
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <AdminUserManager />
              </CardContent>
            </Card>

            {/* Experimental Users Section */}
            <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader className="pb-4">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <span className="text-purple-600 text-lg">🧪</span>
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-gray-900">
                      Experimental Features
                    </CardTitle>
                    <CardDescription className="text-sm text-gray-600">
                      Manage users with access to experimental programs
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <ExperimentalUserManager />
              </CardContent>
            </Card>
          </div>

          {/* Footer */}
          <div className="text-center pt-8">
            <p className="text-xs text-gray-500">
              Admin Dashboard {RELEASE_VERSION} • Secure access management
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminDashboardHeader: React.FC<{handleLogout: () => void}> = ({
  handleLogout,
}) => {
  const { userData } = useUserStore();
  const { name, familyName } = userData;
  return (
    <div className="bg-white/95 backdrop-blur-sm shadow-sm border-b border-gray-200/50 sticky top-0 z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-4 sm:py-6 space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
              <span className="text-white text-sm sm:text-base font-bold">{name?.charAt(0) || 'A'}</span>
            </div>
            <div>
              <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                {name} {familyName}
              </h1>
              <p className="text-xs sm:text-sm text-gray-600">
                Area admin - gestione features ed esperimenti
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800 border border-yellow-200">
              <span className="mr-1">👑</span>
              Admin Mode
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="text-xs sm:text-sm"
            >
              <span className="mr-1">🚪</span>
              Logout
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};